import { AlgoliaSearchParams } from "@omni/types/api/search/algolia/search/algoliaSearchParams.model";
import { SearchResultProductTileConfig } from "@omni/types/components/props/searchResults.props";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import * as SearchSelectors from "@/redux/modules/value/search/search.selectors";
import { SearchStatus } from "@/redux/modules/value/search/search.types";
import { SearchComponent } from "@/utils/search/types/searchComponent";

type SearchResultsContextValue = {
    algoliaSearchParameters?: AlgoliaSearchParams;
    productTileConfig?: SearchResultProductTileConfig;
    showFacets?: boolean;
    showResults?: boolean;
    hasResults: boolean;
};

type SearchResultsContextProviderProps = RequireChildrenFor<{
    algoliaSearchParameters?: AlgoliaSearchParams;
    productTileConfig?: SearchResultProductTileConfig;
    showFacets?: boolean;
}>;

const SEARCH_NO_RESULTS_ID = "searchResults__noResults";

export const SearchResultsContext = createContext<
    SearchResultsContextValue | undefined
>(undefined);
SearchResultsContext.displayName = "SearchResultsContext";

export const SearchResultsContextProvider = ({
    algoliaSearchParameters,
    productTileConfig,
    showFacets,
    children
}: SearchResultsContextProviderProps) => {
    const status = useSelector(SearchSelectors.getSearchStatus);

    const hasProductResults = useSelector(
        SearchSelectors.hasResults(SearchComponent.PRODUCT_NUMBER_OF_HITS)
    );
    const hasContentResults = useSelector(
        SearchSelectors.hasResults(SearchComponent.CONTENT_NUMBER_OF_HITS)
    );

    const hasResults = useMemo(
        () => hasProductResults || hasContentResults,
        [hasProductResults, hasContentResults]
    );

    const showResults = useMemo(
        () => status !== SearchStatus.Loading || hasResults,
        [status, hasResults]
    );

    useEffect(() => {
        const noResultsComponent =
            document.getElementById(SEARCH_NO_RESULTS_ID);

        if (!hasResults && status !== SearchStatus.Loading) {
            noResultsComponent?.classList.remove(
                // TODO - Style
                "searchResults__noResults--hidden"
            );
        } else {
            noResultsComponent?.classList.add(
                // TODO - Style
                "searchResults__noResults--hidden"
            );
        }
    }, [status, hasResults]);

    return (
        <SearchResultsContext
            value={{
                algoliaSearchParameters: algoliaSearchParameters,
                productTileConfig: productTileConfig,
                showFacets: showFacets,
                showResults: showResults,
                hasResults: hasResults
            }}
        >
            {children}
        </SearchResultsContext>
    );
};

export const useSearchResultsContext = () => {
    const context = use<SearchResultsContextValue | undefined>(
        SearchResultsContext
    );

    if (!isDefined(context)) {
        throw new Error(`${SearchResultsContext.displayName} is undefined.`);
    }

    return context;
};
